<template>
  <div>
    <v-card>
      <div class="d-flex align-center justify-space-between">
        <div>
          <v-card-title class="text-no-wrap">
            Retrait
          </v-card-title>
          <v-card-subtitle>Détails sur le retrait</v-card-subtitle>
        </div>

        <v-avatar
          color="primary"
          size="48"
          class="v-avatar-light-bg primary--text me-5"
        >
          <v-icon
            size="30"
            color="primary"
          >
            {{ icons.mdiCashMultiple }}
          </v-icon>
        </v-avatar>
      </div>

      <v-card-text>
        <v-list>
          <v-list-item dense class="px-0 mb-n2">
            <span class="font-weight-medium me-2">
              <v-icon size="15">{{ icons.mdiAccount }}</v-icon> Paramètre:
            </span>
            <span class="text--secondary font-weight-bold" @click="openPartnerDetails(withdrawal.partner)">
              <v-chip pill>
                 {{ withdrawal.partner ? withdrawal.partner.name : 'Unknown' }}
              </v-chip>
            </span>
          </v-list-item>
          <v-list-item dense class="px-0 mb-n2">
            <span class="font-weight-medium me-2">
              <v-icon size="15">{{ icons.mdiCash }}</v-icon> Montant:
            </span>
             <span class="text--secondary font-weight-bold">
              <v-chip color="primary" small>
                {{ formatAmount(withdrawal.amount) }}
              </v-chip>
            </span>
          </v-list-item>
          <v-list-item dense class="px-0 mb-n2">
             <span class="font-weight-medium me-2">
              <v-icon size="15">{{ icons.mdiCalendar }}</v-icon> Date:
            </span>
            <span class="text--secondary font-weight-bold">{{ formattingDate(withdrawal.created_at) }}</span>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions class="justify-center">
        <v-btn
            v-if="canValidate(withdrawal)"
            elevation="6"
            color="primary"
            class="mb-2"
            :loading="loading"
            @click="startAction('validate')"
        >
            <v-icon left>{{ icons.mdiCheck }}</v-icon> Valider
        </v-btn>
        <v-btn
            v-if="canResend(withdrawal)"
            elevation="6"
            color="warning"
            class="mb-2"
            :loading="loading"
            @click="startAction('resend')"
        >
            <v-icon left>{{ icons.mdiRefresh }}</v-icon> Renvoyer le paiement
        </v-btn>
        <v-btn
            v-if="canCheckStatus(withdrawal)"
            elevation="6"
            color="info"
            class="mb-2"
            :loading="loading"
            @click="startAction('check')"
        >
            <v-icon left>{{ icons.mdiInformation }}</v-icon> Vérifier le statut
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="dialogConfirm" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">Confirmation</v-card-title>
        <v-card-text>
            <span v-if="actionType === 'validate'">Voulez-vous vraiment valider ce retrait ?</span>
            <span v-else-if="actionType === 'resend'">Voulez-vous vraiment relancer le paiement pour ce retrait ?</span>
            <span v-else>Voulez-vous vraiment vérifier le statut de ce retrait auprès du fournisseur ?</span>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" text @click="dialogConfirm = false">Non</v-btn>
          <v-btn color="primary" text @click="confirmAction">Oui</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  mdiCashMultiple,
  mdiAccount,
  mdiCash,
  mdiCalendar,
  mdiRefresh,
  mdiCheck,
  mdiInformation
} from '@mdi/js'
import { formatDate } from '@core/date/dateFunctions'
import { intToCurrency } from '@core/dynamicComponents/dynamicComponents'
import store from '@/store'
import router from '@/router'
import { ref } from '@vue/composition-api'
import { notify } from '@core/dynamicComponents/dynamicComponents'

export default {
  name: 'WithdrawalCard',
  props: {
      withdrawal: {
          type: Object,
          required: true
      }
  },
  setup(props, { emit }) {
    const dialogConfirm = ref(false)
    const actionType = ref('validate') // 'validate', 'resend', 'check'
    const loading = ref(false)
    const formattingDate = date => formatDate(date)
    const formatAmount = amount => intToCurrency(store.state.user.lang, {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0,
    }, amount)

    const openPartnerDetails = (partner) => {
        if(partner) router.push({ name: 'partner-details', params: { id: partner.id } })
    }

    const canValidate = (item) => {
        if (!item || !item.statuses) return false;
        const active = item.statuses.find(s => s.pivot.is_active);
        return active && active.code === 'CREATED';
    }

    const canResend = (item) => {
        if (!item || !item.statuses) return false;
        const active = item.statuses.find(s => s.pivot.is_active);
        return active && active.code === 'PAYMENT_FAILED';
    }

    const canCheckStatus = (item) => {
        if (!item || !item.statuses) return false;
        const active = item.statuses.find(s => s.pivot.is_active);
        // Can check for statuses that might be stuck or in progress
        const trackable = ['PAYMENT_STARTED', 'VALIDATED', 'PENDING', 'WAITING'];
        return active && trackable.includes(active.code);
    }

    const startAction = (type) => {
        actionType.value = type
        dialogConfirm.value = true
    }

    const confirmAction = () => {
        dialogConfirm.value = false
        loading.value = true
        
        let action = 'withdraw/validateWithdrawal'
        let successMessage = 'Opération réussie'

        if (actionType.value === 'validate') {
            successMessage = 'Retrait validé avec succès'
        } else if (actionType.value === 'resend') {
            successMessage = 'Paiement relancé avec succès'
        } else if (actionType.value === 'check') {
            action = 'withdraw/verifyStatus'
            successMessage = 'Statut vérifié avec succès'
        }

        store.dispatch(action, props.withdrawal.id).then(() => {
            notify(true, true, successMessage)
            // refresh
            store.dispatch('withdraw/getWithdrawalById', props.withdrawal.id)
            loading.value = false
        }).catch (error => {
             console.error(error)
             notify(true, true, 'Erreur lors de l\'opération')
             loading.value = false
        })
    }

    return {
      formattingDate,
      formatAmount,
      openPartnerDetails,
      canValidate,
      canResend,
      canCheckStatus,
      startAction,
      confirmAction,
      dialogConfirm,
      actionType,
      loading,
      icons: {
        mdiCashMultiple,
        mdiAccount,
        mdiCash,
        mdiCalendar,
        mdiRefresh,
        mdiCheck,
        mdiInformation
      },
    }
  },
}
</script>
